import { create } from 'zustand';
import { uploadFile, errorMessage } from '../api';
import { assetStore } from '../assets';
import { edit } from './store';

export interface UploadItem { key: string; name: string; progress: number; status: 'queued' | 'uploading' | 'done' | 'error'; assetId?: string; error?: string; previewUrl?: string }

export const useUploads = create<{ items: UploadItem[] }>(() => ({ items: [] }));
const MAX_PARALLEL = 3;
const MAX_MB = 60;
let active = 0;
const queue: { key: string; file: File }[] = [];

function update(key: string, patch: Partial<UploadItem>) {
  useUploads.setState((s) => ({ items: s.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) }));
}

/** Queue photos for upload; editing continues while they upload (bounded concurrency). */
export function queuePhotos(files: File[]) {
  const accepted: UploadItem[] = [];
  for (const f of files) {
    const key = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type) && !/\.(jpe?g|png|webp)$/i.test(f.name)) {
      accepted.push({ key, name: f.name, progress: 0, status: 'error', error: 'Not a JPG, PNG or WebP file' });
      continue;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      accepted.push({ key, name: f.name, progress: 0, status: 'error', error: `Larger than ${MAX_MB} MB` });
      continue;
    }
    accepted.push({ key, name: f.name, progress: 0, status: 'queued', previewUrl: URL.createObjectURL(f) });
    queue.push({ key, file: f });
  }
  useUploads.setState((s) => ({ items: [...s.items, ...accepted] }));
  pump();
}

function pump() {
  while (active < MAX_PARALLEL && queue.length) {
    const job = queue.shift()!;
    active++;
    update(job.key, { status: 'uploading' });
    uploadFile<{ asset: Parameters<typeof assetStore.add>[0] }>('/api/assets?kind=photo', job.file, {}, (p) => update(job.key, { progress: p }))
      .then((r) => {
        assetStore.add(r.asset);
        update(job.key, { status: 'done', progress: 1, assetId: r.asset.id });
        edit((d) => (d.photoPool.includes(r.asset.id) ? d : { ...d, photoPool: [...d.photoPool, r.asset.id] }), 'upload');
      })
      .catch((e) => update(job.key, { status: 'error', error: errorMessage(e) }))
      .finally(() => { active--; pump(); });
  }
}

export function clearFinishedUploads() {
  useUploads.setState((s) => {
    s.items.filter((i) => i.status === 'done' && i.previewUrl).forEach((i) => URL.revokeObjectURL(i.previewUrl!));
    return { items: s.items.filter((i) => i.status === 'queued' || i.status === 'uploading') };
  });
}
